import { z } from "zod";
import { nonNegativeNumber, positiveNumber, postiveNumberZeroToOneHundred } from "@/utils/form";
import { t } from "i18next";
import { RevenueCalculationType } from "@/graphql/__generated__/graphql";

export const RevenueCalculationSchema = z.object({
  revenueCalculationType: z.enum([
    RevenueCalculationType.Margin,
    RevenueCalculationType.Flatrate,
    RevenueCalculationType.Hourlyrateabsolute,
    RevenueCalculationType.Hourlyraterelative,
  ], {
    error: (issue) => issue.input === undefined ?
      t("schemas:permanentservice.revenuecalculationtyperequired")
      :
      "", // Type will never be invalid because of the enum
  }),
  margin: postiveNumberZeroToOneHundred(t("permanentservice.inputs.margin.label")).optional(),
  flatRate: nonNegativeNumber(
    t("permanentservice.inputs.flatrate.label")
  ).optional(),
  hourlyRateAbsolute: z
    .object({
      hourlyRateDayAbsolute: positiveNumber(t("permanentservice.inputs.hourlyratedayabsolute.label")),
      hourlyRateNightAbsolute: positiveNumber(t("permanentservice.inputs.hourlyratenightabsolute.label")),
      hourlyRateSundayAbsolute: positiveNumber(t("permanentservice.inputs.hourlyratesundayabsolute.label")),
      hourlyRateSundayNightAbsolute: positiveNumber(t("permanentservice.inputs.hourlyratesundaynightabsolute.label")),
      hourlyRateHolidayAbsolute: positiveNumber(t("permanentservice.inputs.hourlyrateholidayabsolute.label")),
      hourlyRateHolidayNightAbsolute: positiveNumber(t("permanentservice.inputs.hourlyrateholidaynightabsolute.label")),
    }).optional(),
  hourlyRateRelative: z
    .object({
      hourlyRateDayAbsolute: positiveNumber(t("permanentservice.inputs.hourlyratedayabsoluterelative.label")),
      hourlyRateNightRelative: nonNegativeNumber(t("permanentservice.inputs.hourlyratenightrelative.label")),
      hourlyRateSundayRelative: nonNegativeNumber(t("permanentservice.inputs.hourlyratesundayrelative.label")),
      hourlyRateSundayNightRelative: nonNegativeNumber(t("permanentservice.inputs.hourlyratesundaynightrelative.label")),
      hourlyRateHolidayRelative: nonNegativeNumber(t("permanentservice.inputs.hourlyrateholidayrelative.label")),
      hourlyRateHolidayNightRelative: nonNegativeNumber(t("permanentservice.inputs.hourlyrateholidaynightrelative.label")),
    }).optional(),
});

export function ValidateRevenueCalculation(ctx: any) {
  // Validate based on revenueCalculationType
  switch (ctx.value.revenueCalculationType) {
  case RevenueCalculationType.Margin:
    if (ctx.value.margin == null) {
      ctx.issues.push({
        code: "custom",
        path: ["margin"],
        message: t("schemas:permanentservice.marginrequired"),
      });
    }
    if(ctx.value.margin > 99) {
      ctx.issues.push({
        code: "custom",
        path: ["margin"],
        message: t("schemas:permanentservice.marginmax"),
      });
    }
    break;
  case RevenueCalculationType.Flatrate:
    if (ctx.value.flatRate == null) {
      ctx.issues.push({
        code: "custom",
        path: ["flatRate"],
        message: t("schemas:permanentservice.flatraterequired"),
      });
    }
    break;
  case RevenueCalculationType.Hourlyrateabsolute:
    if (ctx.value.hourlyRateAbsolute == null) {
      ctx.issues.push({
        code: "custom",
        path: ["hourlyRateAbsolute"],
        message: t("schemas:permanentservice.hourlyrateabsoluterequired"),
      });
    } else {
      // Validate each field in hourlyRateAbsolute
      const absoluteFields = [
        "hourlyRateDayAbsolute",
        "hourlyRateNightAbsolute",
        "hourlyRateSundayAbsolute",
        "hourlyRateSundayNightAbsolute",
        "hourlyRateHolidayAbsolute",
        "hourlyRateHolidayNightAbsolute",
      ];
      for (const field of absoluteFields) {
        if (ctx.value.hourlyRateAbsolute[field] == null) {
          ctx.issues.push({
            code: "custom",
            path: ["hourlyRateAbsolute", field],
            message: t("form.required"),
          });
        }
      }
    }
    break;
  case RevenueCalculationType.Hourlyraterelative:
    if (ctx.value.hourlyRateRelative == null) {
      ctx.issues.push({
        code: "custom",
        path: ["hourlyRateRelative"],
        message: t("schemas:permanentservice.hourlyraterelativerequired"),
      });
    } else {
      // Validate each field in hourlyRateRelative
      const relativeFields = [
        "hourlyRateDayAbsolute",
        "hourlyRateNightRelative",
        "hourlyRateSundayRelative",
        "hourlyRateSundayNightRelative",
        "hourlyRateHolidayRelative",
        "hourlyRateHolidayNightRelative",
      ];
      for (const field of relativeFields) {
        if (ctx.value.hourlyRateRelative[field] == null) {
          ctx.issues.push({
            code: "custom",
            path: ["hourlyRateRelative", field],
            message: t("form.required"),
          });
        }
      }
    }
    break;
  }
}
